import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "@/components/venture/NotificationBell";
import { VentureKhojLogo } from "@/components/venture/VentureKhojLogo";
import { LanguageSwitcher } from "@/components/venture/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { goToSection } from "@/lib/scroll";
import { startRecommendationFlow, prefetchNextRoutes } from "@/lib/cta";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible, CollapsibleTrigger, CollapsibleContent,
} from "@/components/ui/collapsible";
import api from "@/lib/api";
import {
  Menu, ChevronDown, LayoutDashboard, LogOut, Shield, ShieldCheck, User,
  Sparkles, ArrowRight, Compass, Building2, MapPin, Wallet,
  TrendingUp, FileText, Gift,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Section anchors mirror homepage `<section id>` values.
// When user is on "/", clicking a nav item smooth-scrolls; otherwise → /#anchor.
// ---------------------------------------------------------------------------
// R110.12 — Nav links: keep "What you Get" label; Pricing + Blog stay top-level.
const NAV_LINKS = [
  { key: "how",     label: "How It Works",           anchor: "how-it-works",     href: null },
  { key: "what",    label: "What you Get",           anchor: "reports-overview", href: null },
  { key: "pricing", label: "Pricing",                anchor: null,               href: "/pricing" },
  { key: "blog",    label: "Blog",                   anchor: null,               href: "/blog" },
  { key: "about",   label: "About Us",               anchor: null,               href: "/about" },
];

const INVESTMENT_BANDS = [
  { label: "Under ₹5 Lakh",  href: "/business-ideas/budget/under-5-lakh" },
  { label: "₹5–10 Lakh",     href: "/business-ideas/budget/under-10-lakh" },
  { label: "₹10–25 Lakh",    href: "/business-ideas/budget/under-25-lakh" },
  { label: "₹25–50 Lakh",    href: "/business-ideas/budget/under-50-lakh" },
  { label: "₹50L +",         href: "/business-ideas/budget/above-1-crore" },
];

// ---------------------------------------------------------------------------
// R110.124 — AudienceToggle removed per user request. The existing
// "For Franchisors" link in the right-side login area remains as the
// primary partner-side entry point.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// R110.123 — CityPill removed per user request. `getStoredCity()` util in
// `CitySelector.jsx` is intentionally kept — it's still consumed by the
// business-detail feasibility engine + report renderer.
// ---------------------------------------------------------------------------

export function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const isLanding = loc.pathname === "/";

  const NAV_LINKS_LOCALIZED = [
    { key: "how",       label: t("nav.how_it_works", "How It Works"),                anchor: "how-it-works",     href: null },
    { key: "pricing",   label: t("nav_more.pricing", "Pricing"),                     anchor: null,               href: "/pricing" },
    { key: "blog",      label: t("nav_more.blog", "Blog"),                           anchor: null,               href: "/blog" },
    // R110.98 — "For Franchisors" removed from the primary content nav
    // and re-placed on the right side, adjacent to "Investor login" so
    // both audience entry points cluster together. See render below.
  ];

  const [scrolled, setScrolled] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [ideasOpen, setIdeasOpen] = useState(false);
  const [popular, setPopular] = useState(null);   // null = not loaded
  const [stats, setStats] = useState(null);
  const [activeAnchor, setActiveAnchor] = useState(null);
  // R110.112 — Franchisor brand name for the account dropdown label
  // (replaces the generic "CUSTOMER" / user.name in the top-right menu).
  const [brandName, setBrandName] = useState("");
  useEffect(() => {
    if (user?.role !== "franchisor") { setBrandName(""); return; }
    let cancelled = false;
    api.get("/franchises/mine")
      .then(({ data }) => {
        if (cancelled) return;
        const bn = (data?.franchise?.brand_name || "").trim();
        if (bn) setBrandName(bn);
      })
      .catch(() => { /* silent — falls back to user.name */ });
    return () => { cancelled = true; };
  }, [user?.role, user?.id]);
  const accountLabel = user?.role === "franchisor"
    ? (brandName || user?.name || user?.email || "Franchise")
    : (user?.name || user?.email || "");

  // Sticky shrink on scroll with hysteresis (60px enter / 20px exit) so the
  // header doesn't fluctuate when the user hovers around the threshold or
  // when momentum scroll oscillates.
  useEffect(() => {
    const onScroll = () => {
      setScrolled((prev) => {
        const y = window.scrollY;
        if (!prev && y > 60) return true;
        if (prev && y < 20) return false;
        return prev;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scrollspy — highlight nav link matching the currently visible section.
  useEffect(() => {
    if (!isLanding) { setActiveAnchor(null); return; }
    const ids = NAV_LINKS_LOCALIZED.map((l) => l.anchor).filter(Boolean);
    const observer = new IntersectionObserver(
      (entries) => {
        // Consider the topmost intersecting section as "active".
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveAnchor(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -55% 0px", threshold: 0 },
    );
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [isLanding]);

  // Lazy-load popular + stats the first time the mega-menu opens (desktop) or sheet opens.
  const ensureMenuData = () => {
    if (!popular) {
      api.get("/public/popular-ventures").then((r) => setPopular(r.data?.ventures || [])).catch(() => setPopular([]));
    }
    if (!stats) {
      api.get("/public/site-stats").then((r) => setStats(r.data)).catch(() => setStats({}));
    }
  };

  const smoothOrGo = (link) => {
    setSheetOpen(false);
    setIdeasOpen(false);
    if (link.href) {
      nav(link.href);
      return;
    }
    goToSection({ nav, isLanding, anchor: link.anchor });
  };

  const goRecommendation = () => {
    setSheetOpen(false);
    startRecommendationFlow({ user, nav });
  };

  const barBg = "bg-white/95 border-slate-200 text-slate-900";

  const heightClass = scrolled ? "h-14" : "h-20";
  const linkBase = "text-slate-700 hover:text-slate-900";

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b backdrop-blur-xl transition-all duration-200 ${barBg} ${scrolled ? "shadow-md shadow-black/5" : ""}`}
      data-testid="navbar"
    >
      <div
        className={`mx-auto max-w-7xl px-4 sm:px-8 flex items-center justify-between gap-4 transition-[height] duration-200 ${heightClass}`}
      >
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-brand-link"
          className="flex items-center shrink-0 hover:opacity-90 transition-opacity"
        >
          {/* R124 — "Discover, Compare, Decide" tagline always shown under the wordmark */}
          <VentureKhojLogo variant="dark" size="nav" showTagline />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {/* Business Ideas mega menu (hover) */}
          <div
            className="relative"
            onMouseEnter={() => { setIdeasOpen(true); ensureMenuData(); }}
            onMouseLeave={() => setIdeasOpen(false)}
            data-testid="nav-ideas-wrap"
          >
            <button
              onClick={() => nav("/business-ideas")}
              className={`inline-flex items-center gap-1 rounded-full px-3.5 py-2 text-sm font-medium whitespace-nowrap transition ${linkBase}`}
              data-testid="nav-ideas-trigger"
            >
              {t("nav_more.business_ideas", "Business Ideas")} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${ideasOpen ? "rotate-180" : ""}`} />
            </button>
            {ideasOpen && (
              <MegaMenu
                popular={popular}
                stats={stats}
                onNavigate={(href) => { setIdeasOpen(false); nav(href); }}
                data-testid="nav-ideas-mega"
              />
            )}
          </div>

          {NAV_LINKS_LOCALIZED.map((l) => {
            const isActive = l.anchor
              ? activeAnchor === l.anchor
              : (l.href && loc.pathname.startsWith(l.href));
            return (
              <button
                key={l.key}
                data-testid={`nav-link-${l.key}`}
                data-active={isActive || undefined}
                onClick={() => smoothOrGo(l)}
                className={`relative rounded-full px-3.5 py-2 text-sm font-medium whitespace-nowrap transition ${isActive ? "text-emerald-700 font-semibold" : linkBase}`}
              >
                {l.label}
                {isActive && (
                  <span className="absolute left-3.5 right-3.5 -bottom-0.5 h-0.5 rounded-full bg-emerald-500" />
                )}
              </button>
            );
          })}

          <div className="mx-1.5 h-5 w-px bg-slate-200" />

          <LanguageSwitcher variant="dark" />

          {user ? (
            <>
              <Button
                data-testid="nav-become-partner-btn-auth"
                variant="ghost"
                className="rounded-full text-sm font-medium text-slate-700 whitespace-nowrap"
                onClick={() => nav("/franchise/register")}
              >
                Become a Business Partner
              </Button>
              <UserMenu user={user} onLogout={() => { logout(); nav("/"); }} nav={nav} isLanding={false} accountLabel={accountLabel} />
            </>
          ) : (
            <>
              {/* R110.98 — Cluster the two audience entry points. The
                  "For Franchisors" button sits immediately before
                  "Investor login" so brands never wonder where to
                  land — the login word is what they scan for. */}
              <Button
                data-testid="nav-become-partner-btn"
                variant="ghost"
                className="rounded-full text-sm font-medium text-slate-700 whitespace-nowrap"
                onClick={() => nav("/franchise/register")}
              >
                Become a Business Partner
              </Button>
              <Button
                data-testid="nav-login-btn"
                variant="ghost"
                className="rounded-full text-sm font-medium text-slate-700 whitespace-nowrap"
                onClick={() => nav("/login")}
              >
                Investor login
              </Button>
            </>
          )}

          <Button
            data-testid="nav-primary-cta"
            onClick={goRecommendation}
            onMouseEnter={prefetchNextRoutes}
            onFocus={prefetchNextRoutes}
            className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold whitespace-nowrap h-10 px-4 ml-1 shadow-sm shadow-emerald-500/20"
          >
            <Sparkles className="mr-1.5 h-4 w-4" />
            {user
              ? t("nav_more.go_to_dashboard", "Go to Dashboard")
              : t("nav_more.get_recommendation", "Find My Best-Fit Business")}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </nav>

        {/* Mobile */}
        <div className="flex lg:hidden items-center gap-1">
          <LanguageSwitcher variant="compact" />
          {user && <NotificationBell />}
          <Sheet
            open={sheetOpen}
            onOpenChange={(v) => { setSheetOpen(v); if (v) ensureMenuData(); }}
          >
            <SheetTrigger asChild>
              <Button
                data-testid="nav-mobile-menu-btn"
                variant="ghost"
                size="icon"
                className="rounded-full h-10 w-10 text-slate-900"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[85vw] max-w-[380px] p-0 flex flex-col"
              data-testid="nav-mobile-sheet"
            >
              <SheetHeader className="px-5 pt-5 pb-3 border-b">
                <SheetTitle asChild>
                  <div>
                    <VentureKhojLogo variant="dark" size="md" showTagline />
                  </div>
                </SheetTitle>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto px-2 py-3">
                {/* Business Ideas — collapsible on mobile */}
                <Collapsible>
                  <CollapsibleTrigger
                    data-testid="nav-mobile-ideas-trigger"
                    className="w-full flex items-center justify-between rounded-xl px-3 py-3 hover:bg-slate-50 text-left"
                  >
                    <span className="text-sm font-semibold text-slate-900">{t("nav_more.business_ideas", "Business Ideas")}</span>
                    <ChevronDown className="h-4 w-4 text-slate-500 transition-transform data-[state=open]:rotate-180" />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-3 pb-2">
                    <MobileIdeasSection
                      popular={popular}
                      stats={stats}
                      onNavigate={(href) => { setSheetOpen(false); nav(href); }}
                    />
                  </CollapsibleContent>
                </Collapsible>

                {NAV_LINKS_LOCALIZED.map((l) => (
                  <button
                    key={l.key}
                    data-testid={`nav-mobile-link-${l.key}`}
                    onClick={() => smoothOrGo(l)}
                    className="w-full text-left rounded-xl px-3 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                  >
                    {l.label}
                  </button>
                ))}

                <div className="my-3 h-px bg-slate-200" />

                <div className="px-2">
                  <LanguageSwitcher variant="dark" />
                </div>

                <div className="my-3 h-px bg-slate-200" />

                {user ? (
                  <>
                    {/* Header label — mirrors desktop dropdown */}
                    <div className="px-3 pt-1 pb-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold" data-testid="nav-mobile-menu-label">
                      {accountLabel || user.name || user.email}
                    </div>
                    {user.role === "franchisor" ? (
                      <>
                        <button
                          data-testid="nav-mobile-franchise-dashboard"
                          onClick={() => { setSheetOpen(false); nav("/franchise/dashboard"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <LayoutDashboard className="h-4 w-4" /> Partner Dashboard
                        </button>
                        <button
                          data-testid="nav-mobile-dashboard"
                          onClick={() => { setSheetOpen(false); nav("/dashboard"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Compass className="h-4 w-4" /> Investor Dashboard
                        </button>
                        <button
                          data-testid="nav-mobile-account"
                          onClick={() => { setSheetOpen(false); nav("/account"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <User className="h-4 w-4" /> Account Settings
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          data-testid="nav-mobile-dashboard"
                          onClick={() => { setSheetOpen(false); nav("/dashboard"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <LayoutDashboard className="h-4 w-4" /> Dashboard
                        </button>
                        <button
                          data-testid="nav-mobile-business-profiles"
                          onClick={() => { setSheetOpen(false); nav("/business-profiles"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Building2 className="h-4 w-4" /> My Business Profiles
                        </button>
                        <button
                          data-testid="nav-mobile-reports"
                          onClick={() => { setSheetOpen(false); nav("/my-reports"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <FileText className="h-4 w-4" /> My Reports
                        </button>
                        <button
                          data-testid="nav-mobile-plans"
                          onClick={() => { setSheetOpen(false); nav("/pricing"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <Wallet className="h-4 w-4" /> Plans &amp; Billing
                        </button>
                        <button
                          data-testid="nav-mobile-account"
                          onClick={() => { setSheetOpen(false); nav("/account"); }}
                          className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                        >
                          <User className="h-4 w-4" /> Account Settings
                        </button>
                      </>
                    )}
                    {user.role === "admin" && (
                      <button
                        data-testid="nav-mobile-admin"
                        onClick={() => { setSheetOpen(false); nav("/admin"); }}
                        className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Shield className="h-4 w-4" /> Admin
                      </button>
                    )}
                    <div className="my-2 h-px bg-slate-200" />
                    <button
                      data-testid="nav-mobile-logout"
                      onClick={() => { setSheetOpen(false); logout(); nav("/"); }}
                      className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" /> Log out
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      data-testid="nav-mobile-become-partner"
                      onClick={() => { setSheetOpen(false); nav("/franchise/register"); }}
                      className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50"
                    >
                      Become a Business Partner
                    </button>
                    <button
                      data-testid="nav-mobile-login"
                      onClick={() => { setSheetOpen(false); nav("/login"); }}
                      className="w-full text-left rounded-xl px-3 py-3 text-sm font-medium text-slate-900 hover:bg-slate-50"
                    >
                      Investor login
                    </button>
                  </>
                )}
              </div>

              {/* Fixed primary CTA */}
              <div className="border-t px-4 py-4 bg-white">
                <Button
                  data-testid="nav-mobile-primary-cta"
                  onClick={goRecommendation}
                  className="w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold h-12"
                >
                  <Sparkles className="mr-1.5 h-4 w-4" />
                  {user
                    ? t("nav_more.go_to_dashboard", "Go to Dashboard")
                    : t("nav_more.get_recommendation", "Find My Best-Fit Business")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Desktop mega menu
// ---------------------------------------------------------------------------
function MegaMenu({ popular, stats, onNavigate }) {
  return (
    <div
      className="absolute left-0 top-full pt-3 z-50"
      data-testid="nav-ideas-mega"
    >
      <div className="w-[min(720px,calc(100vw-2rem))] rounded-2xl border border-slate-200 bg-white shadow-xl shadow-black/10 overflow-hidden">
        <div className="grid grid-cols-2 gap-0">
          {/* Column 1 — Browse */}
          <MenuColumn title="Browse">
            <MenuLink icon={Compass} label="Explore All Businesses" onClick={() => onNavigate("/business-ideas")} testid="mega-browse-all" />
            <MenuLink icon={Building2} label="By Industry" onClick={() => onNavigate("/explore")} testid="mega-browse-industry" />
            <MenuLink icon={Wallet} label="By Budget" onClick={() => onNavigate("/business-ideas?view=budget")} testid="mega-browse-budget" />
            <MenuLink icon={MapPin} label="By City" onClick={() => onNavigate("/business-ideas/location")} testid="mega-browse-city" />
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">By Budget</div>
              <div className="grid grid-cols-1 gap-0.5">
                {INVESTMENT_BANDS.map((b) => (
                  <button
                    key={b.href}
                    data-testid={`mega-band-${b.label.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => onNavigate(b.href)}
                    className="rounded-md text-left px-2 py-1 text-xs text-slate-600 hover:bg-emerald-50 hover:text-emerald-800"
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
          </MenuColumn>

          {/* Column 2 — Popular */}
          <MenuColumn title="Popular Businesses" borderX>
            {(popular == null) && (
              <div className="text-xs text-slate-400 py-2">Loading…</div>
            )}
            {popular && popular.length === 0 && (
              <div className="text-xs text-slate-400 py-2">No popular businesses yet.</div>
            )}
            {popular && popular.map((v) => (
              <button
                key={v.slug}
                data-testid={`mega-popular-${v.slug}`}
                onClick={() => onNavigate(`/venture/${v.slug}`)}
                className="w-full text-left rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 flex items-center justify-between gap-2"
              >
                <span className="truncate">{v.name}</span>
                <ArrowRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 transition group-hover:opacity-100 group-hover:translate-x-0" />
              </button>
            ))}
          </MenuColumn>
        </div>
        <button
          data-testid="mega-view-all"
          onClick={() => onNavigate("/business-ideas")}
          className="w-full border-t border-slate-200 bg-slate-50 hover:bg-emerald-50 hover:text-emerald-800 py-3 text-sm font-semibold text-slate-700 flex items-center justify-center gap-1.5"
        >
          Explore all businesses
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function MenuColumn({ title, children, borderX = false }) {
  return (
    <div className={`p-5 ${borderX ? "border-x border-slate-100" : ""}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{title}</div>
      <div className="space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function MenuLink({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full text-left rounded-md px-2 py-1.5 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-800 flex items-center gap-2"
    >
      <Icon className="h-3.5 w-3.5 text-slate-400" />
      {label}
    </button>
  );
}

function StatRow({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-emerald-100">
        <Icon className="h-4 w-4 text-emerald-600" />
      </div>
      <div>
        <div className="font-heading text-lg font-black text-slate-900 leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile ideas section (inside collapsible)
// ---------------------------------------------------------------------------
function MobileIdeasSection({ popular, stats, onNavigate }) {
  return (
    <div className="space-y-1">
      <MobileMenuBtn label="Explore All Businesses" onClick={() => onNavigate("/business-ideas")} testid="mega-mobile-all" />
      <MobileMenuBtn label="By Industry" onClick={() => onNavigate("/explore")} testid="mega-mobile-industry" />
      <MobileMenuBtn label="By Budget" onClick={() => onNavigate("/business-ideas?view=budget")} testid="mega-mobile-budget" />
      <MobileMenuBtn label="By City" onClick={() => onNavigate("/business-ideas/location")} testid="mega-mobile-city" />

      <div className="pt-2 mt-2 border-t border-slate-100">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 mb-1">Budget</div>
        <div className="grid grid-cols-2 gap-1">
          {INVESTMENT_BANDS.map((b) => (
            <button
              key={b.href}
              onClick={() => onNavigate(b.href)}
              className="rounded-md text-left px-2 py-1.5 text-xs text-slate-600 hover:bg-emerald-50 hover:text-emerald-800"
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {popular && popular.length > 0 && (
        <div className="pt-2 mt-2 border-t border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 px-3 mb-1">Popular</div>
          {popular.map((v) => (
            <MobileMenuBtn
              key={v.slug}
              label={v.name}
              onClick={() => onNavigate(`/venture/${v.slug}`)}
              testid={`mega-mobile-popular-${v.slug}`}
            />
          ))}
        </div>
      )}

      {stats && (
        <div className="pt-2 mt-2 border-t border-slate-100 px-3 pb-1 flex gap-3 text-[11px] text-slate-500">
          <span><b className="text-slate-800">{stats.ventures}+</b> businesses</span>
          <span>·</span>
          <span><b className="text-slate-800">{stats.industries}</b> industries</span>
        </div>
      )}
    </div>
  );
}

function MobileMenuBtn({ label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="w-full text-left rounded-lg px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-800"
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Logged-in user menu
// ---------------------------------------------------------------------------
function UserMenu({ user, onLogout, nav, isLanding, accountLabel }) {
  const label = accountLabel || user?.name || user?.email || "";
  const initials = useMemo(() => {
    const n = (label || "U").trim();
    return n.split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  }, [label]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-user-menu-trigger"
          className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1.5 text-sm font-semibold transition ${isLanding ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`}
        >
          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-bold ${isLanding ? "bg-white text-slate-900" : "bg-emerald-600 text-white"}`}>
            {initials}
          </span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-slate-500 text-xs uppercase tracking-widest" data-testid="nav-user-menu-label">
          {label}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {user?.role === "franchisor" ? (
          <>
            {/* R110.111 — Franchisor-specific menu. Investor "Dashboard" is
                still surfaced so a brand contact who wants to browse as an
                investor can switch views without re-logging in. */}
            <DropdownMenuItem data-testid="nav-user-franchise-dashboard" onClick={() => nav("/franchise/dashboard")}>
              <LayoutDashboard className="mr-2 h-4 w-4" /> Partner Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-dashboard" onClick={() => nav("/dashboard")}>
              <Compass className="mr-2 h-4 w-4" /> Investor Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-account" onClick={() => nav("/account")}>
              <User className="mr-2 h-4 w-4" /> Account Settings
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem data-testid="nav-user-dashboard" onClick={() => nav("/dashboard")}>
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-business-profiles" onClick={() => nav("/business-profiles")}>
              <Building2 className="mr-2 h-4 w-4" /> My Business Profiles
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-reports" onClick={() => nav("/my-reports")}>
              <FileText className="mr-2 h-4 w-4" /> My Reports
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-plans" onClick={() => nav("/pricing")}>
              <Wallet className="mr-2 h-4 w-4" /> Plans &amp; Billing
            </DropdownMenuItem>
            {/* R106 — Refer & Earn removed from login dropdown per user request.
                Referrals feature still lives at /account#referrals if we bring it
                back later. */}
            <DropdownMenuItem data-testid="nav-user-account" onClick={() => nav("/account")}>
              <User className="mr-2 h-4 w-4" /> Account Settings
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="nav-user-consent-log" onClick={() => nav("/account/consent-log")}>
              <ShieldCheck className="mr-2 h-4 w-4" /> Consent &amp; Privacy
            </DropdownMenuItem>
          </>
        )}
        {user?.role === "admin" && (
          <DropdownMenuItem data-testid="nav-user-admin" onClick={() => nav("/admin")}>
            <Shield className="mr-2 h-4 w-4" /> Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="nav-user-logout" onClick={onLogout} className="text-rose-600">
          <LogOut className="mr-2 h-4 w-4" /> Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
